import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAnnouncements } from '@/hooks/useApi';

const CATEGORIES = ['all', 'general', 'competition', 'registration', 'transportation', 'important'] as const;

export default function AnnouncementsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<string>('all');
  const { data, isLoading } = useAnnouncements(page, category);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="section-title">{t('announcements.title')}</h1>
      <p className="section-subtitle">{t('announcements.subtitle')}</p>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => { setCategory(cat); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              category === cat
                ? 'bg-primary text-white'
                : 'bg-bg-secondary text-text-secondary hover:bg-bg-card'
            }`}
          >
            {t(`announcements.categories.${cat}`)}
          </button>
        ))}
      </div>

      {/* Announcement Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-40 bg-bg-secondary rounded mb-4" />
              <div className="h-4 bg-bg-secondary rounded w-1/4 mb-3" />
              <div className="h-5 bg-bg-secondary rounded w-3/4 mb-2" />
              <div className="h-4 bg-bg-secondary rounded w-full" />
            </div>
          ))}
        </div>
      ) : data?.items && data.items.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.items.map((announcement) => (
              <Link key={announcement.id} to={`/announcements/${announcement.id}`} className="card group">
                {announcement.imageUrl && (
                  <div className="w-full h-40 bg-bg-secondary rounded-md mb-4 overflow-hidden">
                    <img
                      src={announcement.imageUrl}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                )}
                <span className="badge text-xs mb-2">{announcement.category}</span>
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary transition-colors">
                  {announcement.title}
                </h3>
                <p className="text-text-secondary text-sm line-clamp-3">{announcement.excerpt}</p>
                <p className="text-xs text-text-secondary mt-3">
                  {t('announcements.postedOn')}: {announcement.createdAt}
                </p>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-10">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary !px-3 !py-2 disabled:opacity-40"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-text-secondary">
                {page} / {data.pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="btn-secondary !px-3 !py-2 disabled:opacity-40"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 text-text-secondary">{t('common.noData')}</div>
      )}
    </div>
  );
}
